import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, ShoppingBag, TrendingUp, Users, CreditCard, 
  Banknote, QrCode, AlertTriangle, Package, ArrowUpRight,
  Clock, Calendar, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPOSDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pos/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const paymentData = {
    CASH: { icon: Banknote, color: 'emerald', label: 'Cash' },
    UPI: { icon: QrCode, color: 'violet', label: 'UPI' },
    CARD: { icon: CreditCard, color: 'blue', label: 'Card' }
  };

  return (
    <div className="space-y-6" data-testid="admin-pos-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">POS Dashboard</h1>
          <p className="text-gray-500">Point of Sale system overview</p>
        </div>
        <Link to="/pos" target="_blank">
          <Button className="bg-emerald-600 hover:bg-emerald-700">
            Open POS Terminal
            <ArrowUpRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Today's Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <span className="flex items-center text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
              <Clock className="w-3 h-3 mr-1" /> Today
            </span>
          </div>
          <p className="text-sm text-gray-500">Today's Sales</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{(dashboard?.today?.sales || 0).toLocaleString('en-IN')}
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500">Today's Orders</p>
          <p className="text-2xl font-bold text-gray-900">{dashboard?.today?.orders || 0}</p>
        </div>

        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-violet-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500">Avg Order Value</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{Math.round(dashboard?.today?.avg_order_value || 0).toLocaleString('en-IN')}
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
              This Month
            </span>
          </div>
          <p className="text-sm text-gray-500">Monthly Sales</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{(dashboard?.month?.sales || 0).toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      {/* Payment Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Today's Payment Breakdown</h3>
          <div className="space-y-4">
            {dashboard?.payment_breakdown?.map((payment) => {
              const config = paymentData[payment._id] || { icon: CreditCard, color: 'gray', label: payment._id };
              const Icon = config.icon;
              const percentage = dashboard.today?.sales > 0 
                ? ((payment.amount / dashboard.today.sales) * 100).toFixed(1)
                : 0;
              
              return (
                <div key={payment._id} className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg bg-${config.color}-100 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${config.color}-600`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{config.label}</span>
                      <span className="text-sm text-gray-600">₹{payment.amount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-${config.color}-500 rounded-full`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{payment.count} orders ({percentage}%)</p>
                  </div>
                </div>
              );
            })}
            
            {(!dashboard?.payment_breakdown || dashboard.payment_breakdown.length === 0) && (
              <p className="text-gray-500 text-center py-4">No sales today</p>
            )}
          </div>
        </div>

        {/* Stock Alerts */}
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Stock Alerts</h3>
            <span className="text-sm text-red-600 bg-red-50 px-2 py-1 rounded-full">
              {dashboard?.out_of_stock_count || 0} out of stock
            </span>
          </div>
          
          <div className="space-y-3">
            {dashboard?.low_stock_products?.map((product) => (
              <div key={product.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                    {product.name}
                  </span>
                </div>
                <span className="text-sm font-semibold text-yellow-700">
                  {product.stock} left
                </span>
              </div>
            ))}
            
            {(!dashboard?.low_stock_products || dashboard.low_stock_products.length === 0) && (
              <div className="text-center py-4 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No low stock alerts</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl p-6 border shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Recent POS Orders</h3>
          <Link to="/admin/pos/orders" className="text-emerald-600 hover:text-emerald-700 text-sm flex items-center">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500 border-b">
                <th className="pb-3 font-medium">Order #</th>
                <th className="pb-3 font-medium">Customer</th>
                <th className="pb-3 font-medium">Items</th>
                <th className="pb-3 font-medium">Payment</th>
                <th className="pb-3 font-medium">Total</th>
                <th className="pb-3 font-medium">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard?.recent_orders?.map((order) => (
                <tr key={order._id} className="text-sm">
                  <td className="py-3 font-medium">{order.order_number}</td>
                  <td className="py-3">{order.customer_phone}</td>
                  <td className="py-3">{order.items?.length || 0} items</td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      order.payment_method === 'CASH' ? 'bg-emerald-100 text-emerald-700' :
                      order.payment_method === 'UPI' ? 'bg-violet-100 text-violet-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {order.payment_method}
                    </span>
                  </td>
                  <td className="py-3 font-semibold">₹{order.grand_total?.toFixed(2)}</td>
                  <td className="py-3 text-gray-500">
                    {new Date(order.created_at).toLocaleTimeString('en-IN', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </td>
                </tr>
              ))}
              
              {(!dashboard?.recent_orders || dashboard.recent_orders.length === 0) && (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/admin/pos/managers" className="bg-white rounded-xl p-4 border shadow-sm hover:border-emerald-500 transition-colors">
          <Users className="w-8 h-8 text-emerald-600 mb-2" />
          <p className="font-medium">Store Managers</p>
          <p className="text-sm text-gray-500">Manage POS users</p>
        </Link>
        
        <Link to="/admin/pos/settings" className="bg-white rounded-xl p-4 border shadow-sm hover:border-emerald-500 transition-colors">
          <QrCode className="w-8 h-8 text-violet-600 mb-2" />
          <p className="font-medium">POS Settings</p>
          <p className="text-sm text-gray-500">UPI, GST, WhatsApp</p>
        </Link>
        
        <Link to="/admin/pos/discounts" className="bg-white rounded-xl p-4 border shadow-sm hover:border-emerald-500 transition-colors">
          <DollarSign className="w-8 h-8 text-orange-600 mb-2" />
          <p className="font-medium">POS Discounts</p>
          <p className="text-sm text-gray-500">Manage coupons</p>
        </Link>
        
        <Link to="/admin/pos/reports" className="bg-white rounded-xl p-4 border shadow-sm hover:border-emerald-500 transition-colors">
          <TrendingUp className="w-8 h-8 text-blue-600 mb-2" />
          <p className="font-medium">Sales Reports</p>
          <p className="text-sm text-gray-500">Analytics & insights</p>
        </Link>
      </div>
    </div>
  );
}
